import { useState } from 'react';
import { useNavigate } from 'react-router-dom';

import { PbfLogo } from '../components/PbfLogo';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { getMe, login } from '../services/authService';
import { useAuthStore } from '../store/authStore';

export const Login = () => {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const { setAuth, setUser } = useAuthStore();
  const navigate = useNavigate();

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    setLoading(true);
    try {
      const { token, user, requiresPasswordReset } = await login(email, password);
      setAuth(token, user, requiresPasswordReset);
      if (!requiresPasswordReset) {
        const fullUser = await getMe();
        setUser(fullUser, false);
      }
      navigate(requiresPasswordReset ? '/reset-password' : '/');
    } catch (err: any) {
      setError(err.response?.data?.message || 'Login failed. Please check your credentials.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div
      className="min-h-screen flex items-center justify-center p-4"
      style={{ background: 'linear-gradient(135deg, #151029 0%, #2D2144 60%, #1a0d3a 100%)' }}
    >
      <div className="w-full max-w-md">
        {/* Brand mark */}
        <div className="text-center mb-8">
          <div className="flex justify-center mb-3">
            <PbfLogo className="h-14 w-auto" />
          </div>
          <p className="mt-1 text-sm" style={{ color: 'rgba(255,255,255,0.45)' }}>
            Reporting Portal for Playbook Football Operators
          </p>
        </div>

        <Card
          className="border-0 shadow-2xl"
          style={{
            background: 'rgba(255,255,255,0.06)',
            backdropFilter: 'blur(24px)',
            borderColor: 'rgba(255,255,255,0.10)',
            border: '1px solid rgba(255,255,255,0.10)',
          }}
        >
          <CardHeader className="pb-4">
            <CardTitle className="text-white text-xl">Sign in</CardTitle>
            <CardDescription style={{ color: 'rgba(255,255,255,0.45)' }}>
              Enter your credentials to access the portal
            </CardDescription>
          </CardHeader>
          <CardContent>
            <form onSubmit={handleSubmit} className="space-y-4">
              <div className="space-y-1.5">
                <Label htmlFor="email" style={{ color: 'rgba(255,255,255,0.75)' }}>
                  Email address
                </Label>
                <Input
                  id="email"
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder="you@example.com"
                  required
                  autoComplete="email"
                  style={{
                    background: 'rgba(255,255,255,0.08)',
                    borderColor: 'rgba(255,255,255,0.15)',
                    color: 'white',
                  }}
                  className="placeholder:text-white/30 focus-visible:ring-purple-500"
                />
              </div>

              <div className="space-y-1.5">
                <Label htmlFor="password" style={{ color: 'rgba(255,255,255,0.75)' }}>
                  Password
                </Label>
                <Input
                  id="password"
                  type="password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  required
                  autoComplete="current-password"
                  style={{
                    background: 'rgba(255,255,255,0.08)',
                    borderColor: 'rgba(255,255,255,0.15)',
                    color: 'white',
                  }}
                  className="focus-visible:ring-purple-500"
                />
              </div>

              {error && (
                <div
                  className="text-sm p-3 rounded-lg"
                  style={{
                    background: 'rgba(239,68,68,0.12)',
                    border: '1px solid rgba(239,68,68,0.30)',
                    color: '#fca5a5',
                  }}
                >
                  {error}
                </div>
              )}

              <Button
                type="submit"
                disabled={loading}
                className="w-full font-semibold text-white mt-2"
                style={{
                  background: loading ? 'rgba(140,22,199,0.6)' : '#8C16C7',
                  border: 'none',
                }}
              >
                {loading ? 'Signing in...' : 'Sign in'}
              </Button>
            </form>
          </CardContent>
        </Card>

        {/* <p className="text-center mt-6 text-xs" style={{ color: 'rgba(255,255,255,0.25)' }}>
          Secure access - Operator data is strictly isolated per account
        </p> */}
      </div>
    </div>
  );
};
